import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    getConsent,
    setConsent,
    loadGA,
    trackPageview,
} from "../lib/analytics";

/**
 * Minimal, editorial cookie consent banner.
 * - Shown only until the user has made a choice.
 * - "Acceptér alle" enables statistics cookies and loads GA4.
 * - "Kun nødvendige" stores consent without loading GA4.
 */
const CookieConsent = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const existing = getConsent();
        if (!existing) {
            // Delay slightly so it doesn't fight page load animations
            const t = setTimeout(() => setVisible(true), 700);
            return () => clearTimeout(t);
        }
        // If consent already exists and includes statistics → boot GA4
        if (existing.statistics) {
            loadGA();
            trackPageview(window.location.pathname);
        }
    }, []);

    const acceptAll = () => {
        setConsent({ necessary: true, statistics: true });
        loadGA();
        trackPageview(window.location.pathname);
        setVisible(false);
    };

    const onlyNecessary = () => {
        setConsent({ necessary: true, statistics: false });
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 md:px-8 md:pb-8"
            role="dialog"
            aria-live="polite"
            aria-label="Cookie-samtykke"
        >
            <div className="max-w-[1440px] mx-auto bg-[#0F0F0E] text-[#EBE8E3] border border-[#D9D4CD]/15 shadow-2xl">
                <div className="px-6 md:px-10 py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start md:items-center">
                    <div className="md:col-span-8">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                            Cookies
                        </div>
                        <p className="font-serif text-xl md:text-2xl leading-relaxed text-[#EBE8E3] max-w-2xl">
                            Vi bruger cookies til at forbedre din oplevelse og
                            analysere trafik på hjemmesiden.
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-[#B8B3AC] max-w-2xl">
                            Nødvendige cookies er påkrævet for, at siden
                            fungerer. Statistik-cookies hjælper os med at forstå,
                            hvordan siden bruges, så vi kan forbedre den.
                            Læs mere i vores{" "}
                            <Link
                                to="/cookie-politik"
                                className="underline underline-offset-4 hover:text-white"
                                data-testid="cookie-consent-policy-link"
                            >
                                cookie-politik
                            </Link>
                            .
                        </p>
                    </div>

                    <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3">
                        <button
                            type="button"
                            onClick={acceptAll}
                            data-testid="cookie-accept-all"
                            className="w-full px-6 py-4 text-[11px] tracking-luxury uppercase bg-[#EBE8E3] text-[#0F0F0E] hover:bg-white transition-colors"
                        >
                            Acceptér alle
                        </button>
                        <button
                            type="button"
                            onClick={onlyNecessary}
                            data-testid="cookie-only-necessary"
                            className="w-full px-6 py-4 text-[11px] tracking-luxury uppercase border border-[#D9D4CD]/30 text-[#EBE8E3] hover:border-[#D9D4CD]/60 transition-colors"
                        >
                            Kun nødvendige
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
